import React from 'react';
import ScheduleIcon from '@mui/icons-material/ScheduleOutlined';
import { Box, Chip, Tooltip } from '@mui/material';

import type { AttentionDisposition, EraTriage } from '../../../store';

import { getLaneColor, getLaneLabel } from './attentionLane';

interface AttentionPulseDotProps {
  triage: EraTriage;
  size?: number;
}

function formatSnoozeDate(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface AttentionDispositionChipProps {
  disposition: AttentionDisposition;
}

/**
 * Pill that describes the user's current disposition on a triaged claim.
 * Returns null when the disposition is effectively inactive (expired snooze, empty).
 */
export const AttentionDispositionChip: React.FC<AttentionDispositionChipProps> = ({ disposition }) => {
  if (disposition.dismissed_at) {
    return (
      <Chip
        label="Dismissed"
        size="small"
        sx={{
          height: 20,
          fontSize: '0.6875rem',
          fontWeight: 500,
          bgcolor: 'grey.100',
          color: 'text.disabled',
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    );
  }
  if (disposition.snoozed_until && new Date(disposition.snoozed_until) > new Date()) {
    return (
      <Chip
        icon={<ScheduleIcon sx={{ fontSize: 12, ml: '4px !important', mr: '-2px !important' }} />}
        label={`Snoozed until ${formatSnoozeDate(disposition.snoozed_until)}`}
        size="small"
        sx={{
          height: 20,
          fontSize: '0.6875rem',
          fontWeight: 500,
          bgcolor: '#FFFBEB',
          color: '#92400E',
          '& .MuiChip-icon': { color: '#92400E' },
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    );
  }
  return null;
};

export const AttentionPulseDot: React.FC<AttentionPulseDotProps> = ({ triage, size = 8 }) => {
  const color = getLaneColor(triage.attention_lane);
  const label = getLaneLabel(triage.attention_lane);
  const signals = triage.signals?.length ? ` · ${triage.signals.join(', ')}` : '';
  const tooltipText = `${label}${signals}`;

  return (
    <Tooltip title={tooltipText} arrow placement="top">
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          bgcolor: color,
          flexShrink: 0,
        }}
      />
    </Tooltip>
  );
};
